/**
 * NPM Modules
 */
// const adapter = require('webrtc-adapter');
/**
 * Internal Modules
 */
const logger = require('./logger');
const peer_connection = require('./peer_connection');
const events = require('./events');
/**
 * Module Variables
 */
const TAG = '[media_manager]';
let localStreams = [];
let localScreenShareStream;
let devicePreviewStream = null;
let localPreviewConfig = null;
let gotLocalStreamCallback = ()=>{};
let screenShared = false;
let screenShareExtensionInstalled = false;
let event_manager;
let audioMute = false;
/**
 * Module Functions
 */

/**
 * A simple setter - sets the event_manager for this module. (pass the event_manager from switchrtc_core)
 * @param {EventEmitter} em event_manager from switchrtc_core
 */
let init = (em, _gotLocalStreamCallback) => {
    if (em) event_manager = em;
    if(_gotLocalStreamCallback) gotLocalStreamCallback = _gotLocalStreamCallback;
};

/**
 * This function will call getUserMedia for you. with a spesific audio/video sources.
 * The stream will return to you in the callback function you pass in. no sources - will call default
 * devices.
 * @param  {String}   audioSource audio device id
 * @param  {String}   videoSource video device id
 * @param  {Function} callback    callback function that will receive the MediaStream or null on fail
 */
let startLocalMedia = (sendAudio, audioSource, sendVideo, videoSource, hdVideo, mediaConstraints ,callback, isPreview) => {
    logger.debug(TAG, 'Getting User Media: sendAudio: ', sendAudio, ' sendVideo: ', sendVideo);
    logger.debug(TAG, 'audioSource: ', audioSource, ' videoSource: ', videoSource, ' hdVideo', hdVideo);
    let streamConfig = {
        sendAudio: sendAudio,
        audioSource: audioSource,
        sendVideo: videoSource,
        hdVideo: hdVideo,
        mediaConstraints: mediaConstraints
    };
    let stream = _getLocalStreamByConfig(streamConfig);
    if (stream) {
        _getMediaSuccess(stream, null, callback);
        return;
    }
    let constraints = {
        audio: !sendAudio ? false : {
            deviceId: audioSource ? {
                exact: audioSource
            } : undefined
        },
        video: !sendVideo ? false : {
            deviceId: videoSource ? {
                exact: videoSource
            } : undefined
        }
    };
    if (hdVideo && constraints.video) {
        constraints.video.width = {
            min: 1280,
            ideal: 1920,
            max: 1920
        };
        constraints.video.height = {
            min: 720,
            ideal: 1080,
            max: 1080
        };
    }
    if(mediaConstraints){
      logger.info(TAG, 'Using custom mediaConstraints. Will override defaults if needed');
      for(let k in mediaConstraints) constraints[k] = mediaConstraints[k];

      // override deviceIds with passed parameters
      if (constraints.audio && sendAudio && audioSource) {
        constraints.audio.deviceId = { exact: audioSource };
      }

      if (constraints.video && sendVideo && videoSource) {
        constraints.video.deviceId = { exact: videoSource };
      }
    }

    //stereo works only if echo cancellation is off
    //constraints.audio.mandatory = { echoCancellation: false};



    if (navigator.mediaDevices.getUserMedia) {
        navigator.mediaDevices.getUserMedia(constraints).then((stream) => {
            _getMediaSuccess(stream, streamConfig, callback, isPreview);
        }).catch((err) => {
            _getMediaFailure(err, callback);
        });
    } else {
        logger.error(TAG, 'Your browser does not support getUserMedia API');
        callback(null);
    }
};

/**
 * Inner function to help stop all local media steams. will remove the stream from the peerConnection
 * @event LOCAL_STREAM_STOPPED
 */
let stopLocalMedia = () => {
    if (localStreams.length > 0) {
        localStreams.forEach((s) => {
            s.stream.getTracks()
                .forEach(function(track) {
                    track.stop();
                });
            logger.debug(TAG, 'stopLocalMedia removing local stream', s);
            peer_connection.getPeerConnection()
                .removeStream(s.stream);
            if (event_manager) event_manager.emit(events.LOCAL_STREAM_STOPPED, s.id);
        });
        localStreams = [];
    }
};

let stopLocalStreamById = (id) => {
    var stream = _getLocalStreamById(id);
    if (stream) {
        stream.getTracks()
            .forEach(function(track) {
                track.stop();
            });
        peer_connection.getPeerConnection()
            .removeStream(stream);
        if (event_manager) event_manager.emit(events.LOCAL_STREAM_STOPPED, id);
        localStreams = localStreams.filter((s)=>s.id !== id);
    } else event_manager.emit(events.ERROR, 'No local stream with id ' + id);
};


/**
 * This function is used to change you input devices for the local stream.
 * Will remove your current tracks, and create a new MediaStream from your wanted devices
 * @param  {String}   audioSource audio device id
 * @param  {String}   videoSource video device id
 * @param  {Function} callback    callback function that will receive the MediaStream or null on fail
 */
let changeDevice = (streamId, sendAudio, audioSource, sendVideo, videoSource, hdVideo, mediaConstraints, callback) => {
    let stream = _getLocalStreamById(streamId);
    if(!stream){
      if(event_manager) event_manager.emit(events.ERROR, 'No on going stream with id: ' + streamId);
      return;
    }
    stopLocalStreamById(streamId);
    startLocalMedia(sendAudio, audioSource, sendVideo, videoSource, hdVideo, mediaConstraints, callback);
};

/**
 * Clears you local stream.
 */
let clear = () => {
    logger.debug(TAG, 'Cleaning All Local Media Streams');
    stopLocalMedia();
    stopLocalDevicePreview();
    _stopScreenShare();
    //TODO - Clear the Screen Share!
};

/**
 * Will turn your local video on/off
 */
let toggleVideoMute = (streamId, isMute) => {
    return new Promise((resolve, reject)=>{
      if(!streamId){
        localStreams.forEach((s)=>{
          let stream = s.stream;
          if (stream) {
            let videoTracks = stream.getVideoTracks();
            for (let i = 0; i < videoTracks.length; ++i) {
                videoTracks[i].enabled = !isMute;
            }
            resolve(isMute);
          } else {
            reject('Error while toggle video mute of stream: ' + s);
          }
        });
      } else {
        let stream = _getLocalStreamById(streamId);
        if (stream) {
          let videoTracks = stream.getVideoTracks();
          for (let i = 0; i < videoTracks.length; ++i) {
              videoTracks[i].enabled = !isMute;
          }
          resolve(isMute);
        } else {
          reject('No stream with id ' + streamId + ' found.');
        }
      }
    });
};

/**
 * will toggle yout local audio on/off
 */
let toggleAudioMute = (streamId ,isMute) => {
    return new Promise((resolve, reject)=>{
      if(!streamId){
          localStreams.forEach((s)=>{
            let stream = s.stream;
            if (stream) {
              var audioTracks = stream.getAudioTracks();
              for (var i = 0; i < audioTracks.length; ++i) {
                audioTracks[i].enabled = !isMute;
              }
              resolve(isMute);
            } else {
              reject('Error while toggle audio mute of stream: ' + s);
            }
          });
      }else{
        let stream = _getLocalStreamById(streamId);
        if (stream) {
          var audioTracks = stream.getAudioTracks();
          for (var i = 0; i < audioTracks.length; ++i) {
            audioTracks[i].enabled = !isMute;
          }
          resolve(isMute);
        } else {
          reject('No stream with id ' + streamId + ' found.');
        }
      }
    });
};

/**
 * Thi will turn screenShare on/off
 * @param  {Function} callback will receive true on success, false on fail
 */
let toggleScreenShare = (callback) => {
    if (!screenShared) {
        if(adapter.browserDetails && adapter.browserDetails.browser === 'chrome'){
          if (!screenShareExtensionInstalled) {
              logger.debug(TAG, 'Chrome Extension Not Installed...');
              if (callback) callback(false);
              return;
          }
          logger.debug(TAG, 'Starting ScreenShare');
          screenShared = true;
          if (callback) callback(true);
          window.postMessage({
              type: 'SS_UI_REQUEST',
              text: 'start'
          }, '*');
        } else {
          var constraints = {
                audio: false,
                video: {
                  mediaSource: 'screen',
                   mandatory: {
                       maxWidth: 1280,
                       maxHeight: 720
                   },
                   optional: []
                 }
              };
          navigator.mediaDevices.getUserMedia(constraints).then((stream)=>{
            _getScreenShareSuccess(stream);
            screenShared = true;
          });
        }

    } else {
        _stopScreenShare();
        if (callback) callback(true);
    }
};

let getLocalDevicePreview = (sendAudio, audioSource, sendVideo, videoSource, hdVideo, mediaConstraints)=>{
  if(devicePreviewStream){
    stopLocalDevicePreview();
  }
  return new Promise((resolve, reject)=>{
    startLocalMedia(sendAudio, audioSource, sendVideo, videoSource, hdVideo, mediaConstraints, (stream, err)=>{
      if(!stream) reject(err);
      else{
        if(_getLocalStreamById(stream.id)){
          // Stream is Published
          resolve(null);
        }else{
          devicePreviewStream = stream;
          resolve(stream);
        }
      }
    }, true);
  });
};

let stopLocalDevicePreview = ()=>{
  if(devicePreviewStream){
    logger.debug(TAG, 'Stopping Device Preview');
    devicePreviewStream.getTracks()
        .forEach(function(track) {
            track.stop();
        });
    localStreams = localStreams.filter((s)=>s.id !== devicePreviewStream.id);
    devicePreviewStream = null;
    localPreviewConfig = null;
  }
};

let addLocalDevicePreviewToSession = ()=>{
  if(devicePreviewStream && localPreviewConfig){
    let stream = devicePreviewStream;
    let config = localPreviewConfig;
    logger.debug(TAG, 'Adding Preview stream to session');
    localStreams.push({
      stream: stream,
      streamConfig: config,
      id: stream.id
    });
    localPreviewConfig = null;
    devicePreviewStream = null;
    gotLocalStreamCallback(stream, null, false);
  }else{
    logger.warn(TAG, 'Attempted to add a preview stream to session. No Preview found');
  }
}

/**
 * Inner function to help stop the screenShare. will remove the stream from the peerConnection
 * @event SCREEN_SHARE_STOPPED
 */
let _stopScreenShare = () => {
    let streamId;
    if (localScreenShareStream && screenShared) {
        logger.debug(TAG, 'Screen Share Stopped');
        logger.debug(localScreenShareStream);
        streamId = localScreenShareStream.id;
        localScreenShareStream.getTracks()
            .forEach(function(track) {
                track.stop();
            });
        logger.debug(TAG, 'stopScreenShare removing localScreenShareStream', localScreenShareStream);
        peer_connection.removeStream(localScreenShareStream);
        localScreenShareStream = null;
        screenShared = false;
        if (event_manager) event_manager.emit(events.SCREEN_SHARE_STOPPED, streamId);
    }
};


// listen for messages from the content-script
window.addEventListener('message', (event) => {
    if (event.origin !== window.location.origin) {
        return;
    }

    // content-script will send a 'SS_PING' msg if extension is installed
    if (event.data.type && (event.data.type === 'SS_PING')) {
        screenShareExtensionInstalled = true;
    }

    // user chose a stream
    if (event.data.type && (event.data.type === 'SS_DIALOG_SUCCESS')) {
        _startScreenStreamFrom(event.data.streamId);
    }

    // user clicked on 'cancel' in choose media dialog
    if (event.data.type && (event.data.type === 'SS_DIALOG_CANCEL')) {
        screenShared = false;
        //
        //TODO Emit SCREEN_SHARE_DIALOG_CANCELE !!!
        event_manager.emit(events.SCREEN_SHARE_DIALOG_CANCELED);
    }
});

/**
 * Strats the screenShare from the mediasourceId (streamId).
 * @param  {String} streamId chromeMediaSourceId
 */
let _startScreenStreamFrom = (streamId) => {
    logger.debug(TAG, 'Starting ScreenShare from streamId of', streamId);
    let constraints = {
        audio: false,
        video: {
            mandatory: {
                chromeMediaSource: 'desktop',
                chromeMediaSourceId: streamId,
                maxWidth: window.screen.width,
                maxHeight: window.screen.height
            }
        }
    };
    navigator.mediaDevices.getUserMedia(constraints).then(_getScreenShareSuccess).catch(_getScreenShareFailure);
};

/**
 * The callback function to the screenshare function. if success.
 * @param  {MediaStream} stream the screen share stream
 * @event GOT_SCREEN_SHARE_STREAM with the stream as data to the listeners
 */
let _getScreenShareSuccess = (stream) => {
    localScreenShareStream = stream;
    localScreenShareStream.getVideoTracks()[0].onended = _stopScreenShare;
    screenShared = true;
    event_manager.emit(events.GOT_SCREEN_SHARE_STREAM, stream);
};

/**
 * The callback function for the getUserMedia (localStream).
 * @param  {MediaStream}   stream   localStream
 * @param  {Function} callback callback function that will receive the stream object.
 */
let _getMediaSuccess = (stream, streamConfig, callback, isPreview) => {
    logger.debug(TAG, 'Got User Media');
    if(!isPreview){
      if (streamConfig) localStreams.push({
        stream: stream,
        streamConfig: streamConfig,
        id: stream.id
      });
    }else{
      localPreviewConfig = streamConfig;
    }
    if (audioMute) toggleAudioMute(audioMute);
    callback(stream, null, isPreview);
};

/**
 * Generic Error Handler
 * @param  {Error}   err      [description]
 * @param  {Function} callback [description]
 * @return {[type]}            [description]
 */
let _getMediaFailure = (err, callback) => {
    logger.error(TAG, '_getMediaFailure error: ', err);
    callback(null, err);
};

/**
 * Generic Error Handler
 * @param  {Error}   err      [description]
 * @param  {Function} callback [description]
 * @return {[type]}            [description]
 */
let _getScreenShareFailure = (err, callback) => {
    logger.error(TAG, '_getScreenShareFailure error: ', err);
};

let _getLocalStreamByConfig = (streamConfig) => {
    let streamObj = localStreams.filter((strObj) => JSON.stringify(strObj.streamConfig) === JSON.stringify(streamConfig));
    if (streamObj.length > 0) {
        logger.debug(TAG, 'found local stream with same config');
        return streamObj[0].stream;
    } else return null;
};

let _getLocalStreamById = (id) => {
    let streamObj = localStreams.filter((strObj) => JSON.stringify(strObj.id) === JSON.stringify(id));
    if (streamObj.length > 0) {
        logger.debug(TAG, 'found local stream with same id');
        return streamObj[0].stream;
    } else return null;
};

/**
 * Module exported Interface
 */
module.exports = {
    startLocalMedia: startLocalMedia,
    stopLocalMedia: stopLocalMedia,
    stopLocalStreamById:stopLocalStreamById,
    getLocalStreams: () => localStreams,
    getLocalScreenShareStream: () => localScreenShareStream,
    changeDevice: changeDevice,
    clear: clear,
    toggleVideoMute: toggleVideoMute,
    toggleAudioMute: toggleAudioMute,
    toggleScreenShare: toggleScreenShare,
    isScreenShared: () => screenShared,
    init: init,
    getLocalDevicePreview: getLocalDevicePreview,
    stopLocalDevicePreview: stopLocalDevicePreview,
    addLocalDevicePreviewToSession: addLocalDevicePreviewToSession
};



// WEBPACK FOOTER //
// ./src/Modules/media_manager.js